import { z } from 'zod';
/**
 * Describing an email and getting one designed.
 *
 * The conversation is sent whole on every turn rather than kept on the server.
 * Nothing about a half-finished design is worth a table: it is worth something
 * only once it becomes a template or a campaign, and both of those already have
 * one. The cost is a larger request; the saving is a screen with no state to
 * expire, resume, or clean up.
 */
const turnSchema = z.object({
    from: z.enum(['person', 'designer']),
    text: z.string().max(20_000),
});
/**
 * A file attached to a turn.
 *
 * Base64 in the JSON body rather than a multipart upload: the request already
 * carries the whole conversation, these are a few megabytes at most, and
 * nothing is stored — so a second transport, a second route and a temporary
 * file to clean up would all exist to save a third of the bytes.
 *
 * The caps are the domain's numbers, imported rather than restated, so the
 * screen cannot accept a file the server refuses. A refusal after an upload
 * has finished is the worst moment to discover a limit.
 */
/**
 * The same number as the domain's `MOST_REFERENCES`, restated.
 *
 * `packages/contracts` does not depend on `packages/domain` — that is the
 * boundary that keeps the wire shape from dragging the rules across it — so the
 * cap is written twice and held together by a test in `adapter-http`, which is
 * the nearest package that can see both. The same arrangement the choices cap
 * has.
 */
const MOST_REFERENCES = 10;
export const designReferenceSchema = z.object({
    name: z.string().trim().min(1).max(200),
    mediaType: z.string().trim().min(1).max(120),
    data: z.string().min(1),
});
export const designRequestSchema = z.object({
    instruction: z.string().trim().min(1, 'say what you want').max(4_000),
    /** Oldest first. Bounded because a conversation this long has lost its thread. */
    history: z.array(turnSchema).max(40),
    /**
     * The design on screen, so an edit changes it rather than starting again.
     *
     * Null on the first turn, which is the difference between designing and
     * editing — and a distinction the model is told about explicitly.
     */
    current: z.string().max(400_000).nullable(),
    /**
     * Files to design from — a brief, a brand sheet, last year's newsletter.
     *
     * Optional so an existing client keeps working. Defaulted rather than left
     * undefined, because every reader downstream wants a list.
     */
    references: z.array(designReferenceSchema).max(MOST_REFERENCES).default([]),
    /**
     * A website this email should look like it came from.
     *
     * Validated no further than "is a url" here. Whether it is one worth fetching
     * — the scheme, the port, and above all whether it resolves onto this
     * machine's own network — is decided where the request is actually made, and
     * duplicating any of that judgement here would be a second place for it to
     * drift out of agreement with the first.
     */
    siteUrl: z.string().trim().url().max(2_000).optional(),
    /**
     * Which conversation this turn belongs to, or absent to start one.
     *
     * The turn saves itself — there is no separate call — so the reply carries
     * the id back and the next turn quotes it. A conversation that exists before
     * its first answer would be an empty row every time the model was busy.
     */
    conversationId: z.string().uuid().optional(),
});
/**
 * A turn's answer.
 *
 * `html` is null when the reply was a question. Asking "which of the two
 * logos?" is a useful turn, and a contract that demanded markup every time
 * would force a guess instead.
 */
export const designReplySchema = z.object({
    say: z.string(),
    html: z.string().nullable(),
    /** Where this turn was saved. Null when saving it failed — see the route. */
    conversationId: z.string().nullable().default(null),
});
/**
 * What has been learned about how this sender's mail should look.
 *
 * `null` rather than an absent field: "nothing has been learned yet" is the
 * ordinary state on a new organisation and is said plainly, so a screen can
 * offer to teach it rather than showing an empty box that looks broken.
 */
export const brandProfileSchema = z.object({
    brand: z
        .object({
        profile: z.string(),
        /** The files it was learned from, so somebody can tell what taught it. */
        learnedFrom: z.array(z.string()),
        updatedAt: z.string().datetime(),
    })
        .nullable(),
});
/**
 * A conversation, as the list beside the designer shows it.
 *
 * Enough to choose one and nothing more: the turns are fetched when one is
 * opened, because a list of twenty conversations is otherwise twenty
 * conversations' worth of text to draw a sidebar.
 */
export const conversationSummarySchema = z.object({
    conversationId: z.string(),
    title: z.string(),
    updatedAt: z.string().datetime(),
});
export const conversationListSchema = z.object({
    conversations: z.array(conversationSummarySchema),
});
/** Not exported: the conversation below is the only thing that holds one. */
const storedTurnSchema = z.object({
    from: z.enum(['person', 'designer']),
    text: z.string(),
    /** Names only. The files themselves were never stored. */
    attached: z.array(z.string()),
});
export const storedConversationSchema = conversationSummarySchema.extend({
    turns: z.array(storedTurnSchema),
    /** Null when the conversation never produced one — a turn can be a question. */
    design: z.string().nullable(),
});
/**
 * A campaign described in a sentence, on its way to being planned.
 *
 * The brief is capped at a paragraph. It is the one thing somebody types before
 * anything else exists, and a field that accepts an essay invites one — which
 * costs tokens and makes a worse plan, because the useful signal is in the
 * first two lines either way.
 *
 * **The segments are not sent from the browser.** Only the brief is. The server
 * already knows which segments this organisation has, and a request that
 * carried them would be a request that could name somebody else's.
 */
export const campaignPlanRequestSchema = z.object({
    brief: z.string().min(1).max(600),
});
/**
 * What came back: a name, an audience and some subject lines.
 *
 * `segmentId` is null for the whole subscribed list, matching the campaign
 * contract's own spelling of "everyone" so the value can be carried straight
 * across without a translation nobody would remember to keep.
 *
 * Nothing here is applied without a press. The screen fills the name in and
 * offers the rest, because a wrong name is overtyped in a second and a wrong
 * audience is the one mistake a campaign cannot take back.
 */
export const campaignPlanSchema = z.object({
    name: z.string(),
    segmentId: z.string().nullable(),
    why: z.string(),
    subjects: z.array(z.string()).max(3),
});
//# sourceMappingURL=designer.js.map